import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSesion } from '../estado/SesionContext';
import { Bloque } from '../components/Bloque';
import type { Curso as CursoTipo } from '../domain/types';

export function Curso() {
  const { cursoId } = useParams<{ cursoId: string }>();
  const { repo } = useSesion();
  const navegar = useNavigate();

  const [curso, setCurso] = useState<CursoTipo | null>(null);
  const [indice, setIndice] = useState(0);
  const [cargando, setCargando] = useState(true);

  // Carga el curso y retoma donde iba: la primera lección con algún bloque sin ver.
  useEffect(() => {
    if (!repo || !cursoId) return;
    let vivo = true;

    void (async () => {
      const encontrado = await repo.obtenerCurso(cursoId);
      if (!vivo) return;
      setCurso(encontrado);

      if (encontrado) {
        const inscripcion = await repo.obtenerInscripcion(cursoId);
        const vistos = new Set(inscripcion?.bloquesVistos ?? []);
        const pendiente = encontrado.lecciones.findIndex((l) =>
          l.bloques.some((b) => !vistos.has(b.id)),
        );
        if (vivo) setIndice(pendiente === -1 ? 0 : pendiente);
      }
      if (vivo) setCargando(false);
    })();

    return () => {
      vivo = false;
    };
  }, [repo, cursoId]);

  const leccion = curso?.lecciones[indice];

  // Ver una lección marca sus bloques. Se hace acá y no en el botón "Siguiente" para que
  // también cuente la última lección, a la que nadie le da "siguiente".
  useEffect(() => {
    if (!repo || !curso || !leccion) return;
    void (async () => {
      for (const bloque of leccion.bloques) {
        await repo.marcarBloque(curso.id, bloque.id);
      }
    })();
  }, [repo, curso, leccion]);

  if (cargando) return <p className="py-10 text-center text-gris">Cargando…</p>;

  if (!curso || !leccion) {
    return (
      <div className="py-10 text-center">
        <p className="text-3xl">🤔</p>
        <p className="mt-3 font-extrabold">No encontramos ese curso</p>
        <Link to="/" className="mt-4 inline-block text-sm font-bold text-azul underline">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const esUltima = indice === curso.lecciones.length - 1;
  const progreso = Math.round((100 * (indice + 1)) / curso.lecciones.length);

  return (
    <article>
      <Link to="/" className="text-sm font-bold text-azul">
        ← Inicio
      </Link>

      <header className="mt-3">
        <h1 className="font-titulos text-2xl font-extrabold text-tinta">{curso.titulo}</h1>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-cielo">
            <div
              className="h-full rounded-full bg-azul transition-all"
              style={{ width: `${progreso}%` }}
            />
          </div>
          <span className="shrink-0 text-xs font-extrabold text-gris">
            Lección {indice + 1} de {curso.lecciones.length}
          </span>
        </div>
      </header>

      <section className="mt-5 rounded-2xl border border-borde bg-white p-5 sm:p-7">
        <h2 className="font-titulos text-xl font-extrabold text-azul">{leccion.titulo}</h2>
        {leccion.bloques.map((b) => (
          <Bloque key={b.id} bloque={b} />
        ))}
      </section>

      <nav className="mt-6 flex gap-3">
        <button
          onClick={() => setIndice((i) => Math.max(0, i - 1))}
          disabled={indice === 0}
          className="rounded-full border-[1.5px] border-borde px-5 py-3 text-sm font-extrabold text-gris disabled:opacity-40"
        >
          ← Anterior
        </button>

        {esUltima ? (
          <button
            onClick={() => navegar(`/curso/${curso.id}/quiz`)}
            className="flex-1 rounded-full bg-rojo py-3 text-sm font-extrabold text-white transition hover:bg-rojo-osc"
          >
            Ir al quiz 🎯
          </button>
        ) : (
          <button
            onClick={() => {
              setIndice((i) => i + 1);
              window.scrollTo(0, 0);
            }}
            className="flex-1 rounded-full bg-azul py-3 text-sm font-extrabold text-white transition hover:bg-azul-osc"
          >
            Siguiente →
          </button>
        )}
      </nav>
    </article>
  );
}
